import * as forge from "node-forge"
import { API_URL } from "config/environments"
import { createSHA256 } from "hash-wasm"
import { MultipartUploadPath } from "workers/type"
import { getStoreData, Stores } from "services/indexedDB"
import { encryptionHelper } from "./encryption"
import { isEmpty } from "lodash"
import { umamiTracking } from "helpers/umami.client"
import { EVENT } from "constants/events"

export const CHUNK_SIZE_ENCRYPT = 5 * 1024 * 1024 // 5MB
interface Metadata {
  mimeType: string
  encryptVersion: string
}

class EncryptionGCMHelper {
  constructor() {}

  public async createEncryptedChunksStream(
    file: File,
    importedKey: CryptoKey,
    CHUNK_SIZE: number,
    multipartUploadPath: any,
    totalChunks: number
  ) {
    const etags: { PartNumber: number; ETag: string }[] = []
    let offset = 0

    for (let i = 0; i < totalChunks; i++) {
      const partNumber = i + 1
      const chunk = file.slice(offset, offset + CHUNK_SIZE)
      const buffer = await chunk.arrayBuffer()

      // Encrypt chunk
      const iv = crypto.getRandomValues(new Uint8Array(12))
      const encrypted = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        importedKey,
        buffer
      )

      // Prepend IV to encrypted data
      const ivAndEncrypted = new Uint8Array(iv.length + encrypted.byteLength)
      ivAndEncrypted.set(iv, 0)
      ivAndEncrypted.set(new Uint8Array(encrypted), iv.length)

      // Upload to presigned URL
      const partUrl = multipartUploadPath.parts.find(
        (p) => p.partNumber === partNumber
      )?.url
      if (!partUrl) throw new Error(`Missing URL for part ${partNumber}`)

      const uploadRes = await fetch(partUrl, {
        method: "PUT",
        body: ivAndEncrypted,
      })

      const etag = uploadRes.headers.get("etag")
      if (!etag) throw new Error(`Missing ETag for part ${partNumber}`)

      etags.push({ PartNumber: partNumber, ETag: etag.replace(/"/g, "") })
      offset += CHUNK_SIZE
    }
    return etags
  }

  public async coverPrivateKeyToCryptoKey(key): Promise<CryptoKey | null> {
    try {
      const keyData = this.pemToArrayBuffer(key)
      return await crypto.subtle.importKey(
        "pkcs8",
        keyData,
        {
          name: "RSA-PSS",
          hash: "SHA-256",
        },
        false,
        ["sign"]
      )
    } catch (error) {
      return null
    }
  }

  public decryptKeyByCrypto = async (
    hexKey: string,
    type: "hex" | "base64" | "passphrase" = "hex"
  ): Promise<CryptoKey> => {
    const rawKey = await this.normalizeKey(hexKey, type)
    return crypto.subtle.importKey("raw", rawKey, "AES-GCM", false, ["decrypt"])
  }

  public decryptGCMStream(
    originStream: ReadableStream<Uint8Array>,
    cryptoKey: CryptoKey
  ) {
    const reader = originStream.getReader()
    let leftover = new Uint8Array()
    const CHUNK_SIZE = 12 + CHUNK_SIZE_ENCRYPT + 16 // IV + Plaintext + Tag

    async function decryptChunk(chunk: Uint8Array): Promise<Uint8Array> {
      const iv = chunk.slice(0, 12)
      const ciphertext = chunk.slice(12)
      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        cryptoKey,
        ciphertext
      )
      return new Uint8Array(decrypted)
    }

    return new ReadableStream<Uint8Array>({
      async pull(controller) {
        while (true) {
          const { value, done } = await reader.read()

          // If stream ends, decrypt any leftover
          if (done) {
            if (leftover.length > 0) {
              try {
                const decrypted = await decryptChunk(leftover)
                controller.enqueue(decrypted)
              } catch (err) {
                controller.error(err)
                return
              }
            }
            controller.close()
            break
          }

          // Merge leftover with new data
          let combined = new Uint8Array(leftover.length + value!.length)
          combined.set(leftover)
          combined.set(value!, leftover.length)

          let offset = 0
          while (offset + CHUNK_SIZE <= combined.length) {
            const chunk = combined.slice(offset, offset + CHUNK_SIZE)
            try {
              const decrypted = await decryptChunk(chunk)
              controller.enqueue(decrypted)
            } catch (err) {
              controller.error(err)
              return
            }
            offset += CHUNK_SIZE
          }

          // Save unprocessed tail for next pull
          leftover = combined.slice(offset)
        }
      },
    })
  }

  public verifySignatureFileByGCM = async (
    key: string,
    file_key: string,
    token: string,
    fileOption: {
      project_build_id?: string
      conversation_id?: string
    } = {},
    dbKey?: string
  ): Promise<boolean> => {
    console.log("-------START VERIFY SIGNATURE-----------")

    try {
      const fileURL = this.getFullAssetURI(file_key, fileOption)
      const [fileResponse, hashKeyResponse] = await Promise.all([
        fetch(fileURL, {
          headers: {
            Authorization: token,
            ...(dbKey ? { "x-db-key": dbKey } : {}),
          },
        }),
        fetch(
          `${API_URL}/api/file/hash?file_key=${encodeURIComponent(file_key)}`,
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
              ...(dbKey ? { "x-db-key": dbKey } : {}),
            },
          }
        ),
      ])

      if (!fileResponse.body) throw new Error("Empty file stream")
      const { hash_key, public_key } = await hashKeyResponse.json()
      if (!hash_key || !public_key)
        throw new Error("Missing hash key or public key")
      const decryptKey = await this.decryptKeyByCrypto(key)
      const decryptedStream = this.decryptGCMStream(
        fileResponse.body,
        decryptKey
      )
      const publicCryptoKey = await this.coverPublicKeyToCryptoKey(public_key)
      const dataBlob = await new Response(decryptedStream).blob()
      const hashBuffer = await this.calculateSHA256(dataBlob)
      const signatureBuffer = this.base64ToArrayBuffer(hash_key)
      if (!publicCryptoKey) throw new Error("Invalid public key")
      const verified = await crypto.subtle.verify(
        { name: "RSA-PSS", saltLength: 32 },
        publicCryptoKey,
        signatureBuffer,
        hashBuffer
      )

      console.log("Signature", verified ? "//SUCCESS//" : "//FAILED//")
      return verified
    } catch (err) {
      return false
    } finally {
      console.log("-------END VERIFY SIGNATURE-----------")
    }
  }

  public getMessengerFileError = (fileErrors: string[] = []) => {
    return `${fileErrors.map((el) => el.split(" - ")[1]).join(", ")}`
  }

  public getFileImageFromEncrypted = async (
    url: string,
    key: string,
    token: string,
    fileOption = {},
    resutlType: "base64" | "blob" = "blob",
    fileType?: string,
    dbKey?: string
  ) => {
    const fileURL = this.getFullAssetURI(url, fileOption)

    try {
      const response = await fetch(fileURL, {
        headers: {
          Authorization: token,
          ...(dbKey ? { "x-db-key": dbKey } : {}),
        },
      })

      const metadataResponse = response.headers.get("x-meta-data")
      const metadataJson: {
        "mime-type": string
        "encrypt-version": string
      } = metadataResponse ? JSON.parse(metadataResponse) : {}
      if (metadataJson?.["encrypt-version"]) {
        if (!response.body) {
          umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_FAILURE, {
            error: "no_body",
            file_type: "image",
          })
          return null
        }
        const decryptKey = await this.decryptKeyByCrypto(key)
        const decryptedStream = this.decryptGCMStream(response.body, decryptKey)
        const dataBlob = await new Response(decryptedStream, {
          headers: {
            "Content-Type": metadataJson["mime-type"] || "image/svg+xml",
          },
        }).blob()
        if (resutlType === "base64") {
          umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_SUCCESS, {
            file_type: "image",
          })
          return this.blobToBase64(dataBlob)
        }
        umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_SUCCESS, {
          file_type: "image",
        })
        return dataBlob
      }
      const temp = await response.text()
      const dataDecrypt = await encryptionGCMHelper.decryptContext(
        key,
        temp,
        true
      )
      const svgBlob = new Blob(
        [dataDecrypt],
        fileType ? { type: fileType } : {}
      )
      if (resutlType === "base64") {
        umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_SUCCESS, {
          file_type: "image",
        })
        return this.blobToBase64(svgBlob)
      }
      umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_SUCCESS, {
        file_type: "image",
      })
      return svgBlob
    } catch (error: any) {
      umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_FAILURE, {
        error: error?.message ?? "image_decrypt",
        file_type: "image",
      })
      return null
    }
  }

  public getFileBOMFromEncrypted = async (
    url: string,
    key: string,
    token: string,
    fileOption = {},
    decryptedShareKey?: string,
    dbKey?: string
  ) => {
    const fileURL = this.getFullAssetURI(url, fileOption)
    try {
      const response = await fetch(fileURL, {
        headers: {
          Authorization: token,
          ...(dbKey ? { "x-db-key": dbKey } : {}),
        },
      })

      const metadata = this.parseMetadata(response.headers.get("x-meta-data"))
      if (metadata.encryptVersion) {
        if (!response.body) {
          umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_FAILURE, {
            error: "no_body",
            file_type: "bom",
          })
          return null
        }
        const decryptKey = await this.decryptKeyByCrypto(key)
        const decryptedStream = this.decryptGCMStream(response.body, decryptKey)
        const dataBlob = await new Response(decryptedStream, {
          headers: {
            "Content-Type": metadata.mimeType,
          },
        }).blob()
        const arrayBuffer = await dataBlob.arrayBuffer()
        umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_SUCCESS, {
          file_type: "bom",
        })
        return new Uint8Array(arrayBuffer)
      }
      let blob: any = await response.blob()
      let temp = await blob.text()
      const data = await encryptionGCMHelper.decryptContext(
        decryptedShareKey || key,
        temp,
        true
      )
      umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_SUCCESS, { file_type: "bom" })
      return data
    } catch (error: any) {
      umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_FAILURE, {
        error: error?.message ?? "bom_decrypt",
        file_type: "bom",
      })
      return null
    }
  }

  public readAndUploadFileShareComponent = async (
    url: string,
    projectKey: string,
    sharedKey: string,
    token: string,
    projectComponentHistoryId: string,
    userId: string,
    conversationId: string
  ) => {
    console.log("-------START READ FILE ORIGIN SHARE-----------")

    const fileURL = this.getFullAssetURI(url, {})

    const newFileKey = url.replace(
      /^([a-f0-9-]+)\//,
      `$1/share-${projectComponentHistoryId}/`
    )
    const newFileName = this.extractFileName(url)
    const privateKey = await this.getUserPrivateKey(userId)
    if (!privateKey) {
      return {
        success: false,
        message: "Private key not found",
        fileName: newFileName,
        fileHash: "",
      }
    }

    let decryptedFileBlob: Blob | null = null
    try {
      const [fileResponse, hashKeyResponse] = await Promise.all([
        fetch(fileURL, { headers: { Authorization: token } }),
        fetch(`${API_URL}/api/file/hash?file_key=${encodeURIComponent(url)}`, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }),
      ])

      const metadata = this.parseMetadata(
        fileResponse.headers.get("x-meta-data")
      )
      const { hash_key, public_key } = await hashKeyResponse.json()
      if (metadata.encryptVersion) {
        console.log("------- DECRYPT FILE-----------")
        if (!fileResponse.body) {
          return {
            success: false,
            message: "Empty file stream",
            fileName: newFileName,
            fileHash: "",
          }
        }
        const decryptKey = await this.decryptKeyByCrypto(projectKey)
        let decryptedStream: any = this.decryptGCMStream(
          fileResponse.body,
          decryptKey
        )
        const blobGCM = await new Response(decryptedStream, {
          headers: {
            "Content-Type": metadata.mimeType,
          },
        }).blob()
        decryptedStream = null // Clear memory
        if (blobGCM.size === 0) {
          return {
            success: false,
            message: "Decrypted file is empty",
            fileName: newFileName,
            fileHash: "",
          }
        }
        if (hash_key && public_key) {
          const publicCryptoKey = await this.coverPublicKeyToCryptoKey(
            public_key
          )
          if (!publicCryptoKey) {
            return {
              success: false,
              message: "Invalid public key",
              fileName: newFileName,
              fileHash: "",
            }
          }
          const hashBuffer = await this.calculateSHA256(blobGCM)
          const signatureBuffer = this.base64ToArrayBuffer(hash_key)
          const verifiedGCM = await crypto.subtle.verify(
            { name: "RSA-PSS", saltLength: 32 },
            publicCryptoKey,
            signatureBuffer,
            hashBuffer
          )
          console.log("Signature", verifiedGCM ? "//SUCCESS//" : "//FAILED//")
          console.log("-------END READ FILE ORIGIN SHARE-----------")
          if (!verifiedGCM) {
            return {
              success: false,
              message: "Signature verification failed",
              fileName: newFileName,
              fileHash: "",
            }
          }
        }

        decryptedFileBlob = blobGCM
      } else {
        let temp = (await fileResponse.text()) as any
        let dataDecrypt: any = await encryptionGCMHelper.decryptContext(
          projectKey,
          temp,
          true
        )
        const contentType =
          fileResponse.headers.get("content-type") || "application/octet-stream"
        const blobCBC = new Blob([dataDecrypt], { type: contentType })
        dataDecrypt = null // Clear memory
        if (blobCBC.size === 0) {
          return {
            success: false,
            message: `${newFileName} Decrypted file is empty`,
            fileName: newFileName,
            fileHash: "",
          }
        }
        let blobText: any = await blobCBC.text()
        if (hash_key && public_key) {
          const verifiedCBC = encryptionHelper.checkVerifiedSignature(
            public_key,
            hash_key,
            blobText
          )
          blobText = null // Clear memory
          console.log("Signature", verifiedCBC ? "//SUCCESS//" : "//FAILED//")
          console.log("-------END READ FILE ORIGIN SHARE-----------")
          if (!verifiedCBC) {
            return {
              success: false,
              message: "Signature verification failed",
              fileName: newFileName,
              fileHash: "",
            }
          }
        }
        decryptedFileBlob = blobCBC
      }
      if (!decryptedFileBlob) {
        return {
          success: false,
          message: `${newFileName} Decrypted file is empty`,
          fileName: newFileName,
          fileHash: "",
        }
      }
      console.log("-------START ENCRYPT FILE SHARE-----------")
      let fileShare: any = new File([decryptedFileBlob], newFileName, {
        type: decryptedFileBlob.type,
      })
      decryptedFileBlob = null // Clear memory
      const fileShareHex = await this.getFileHash(fileShare)
      const fileShareHashBuffer = new TextEncoder().encode(fileShareHex)
      const fileShareSignature = await this.createSignature(
        privateKey,
        fileShareHashBuffer
      )
      const fileShareSignatureBase64 = this.bufferToBase64(fileShareSignature)

      const totalChunks = Math.ceil(fileShare.size / CHUNK_SIZE_ENCRYPT)

      const multipartUploadPath = await this.getMultipartUploadPath(token, {
        fileName: newFileKey,
        fileType: fileShare.type || "application/octet-stream",
        totalChunks,
        componentHistoryId: projectComponentHistoryId,
        componentType: "share-component",
      })
      if (!multipartUploadPath?.key || !multipartUploadPath?.uploadId) {
        return {
          success: false,
          message: "Failed to get multipart upload path",
          fileName: newFileName,
          fileHash: "",
        }
      }
      const encryptKey = await this.importAESKey(sharedKey)

      const etags = await this.createEncryptedChunksStream(
        fileShare,
        encryptKey,
        CHUNK_SIZE_ENCRYPT,
        multipartUploadPath,
        totalChunks
      )
      fileShare = null // Clear memory
      const mergeSuccess = await this.mergeUploadedChunks(
        token,
        multipartUploadPath,
        etags
      )
      if (!mergeSuccess) {
        return {
          success: false,
          message: "Failed to merge uploaded chunks",
          fileName: newFileName,
          fileHash: "",
        }
      }

      const response = await fetch(
        `${API_URL}/api/project-build/invitee/upload-file`,
        {
          method: "POST",
          body: JSON.stringify({
            file_hash: fileShareSignatureBase64,
            file_name: newFileKey,
          }),
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )

      if (!response.ok) {
        return {
          success: false,
          message: "Failed to upload file share",
          fileName: newFileName,
          fileHash: "",
        }
      }
      const fileShareVerify = await this.verifySignatureFileByGCM(
        sharedKey,
        newFileKey,
        token,
        {
          conversation_id: conversationId,
        }
      )
      if (!fileShareVerify) {
        return {
          success: false,
          message: "File share signature verification failed",
          fileName: newFileName,
          fileHash: "",
        }
      }
      return {
        success: true,
        message: "File share uploaded successfully",
        fileName: newFileKey,
        fileHash: fileShareSignatureBase64,
      }
    } catch (err) {
      return {
        success: false,
        message: `Error processing file share`,
        fileName: newFileName,
        fileHash: "",
      }
    } finally {
      console.log("-------END ENCRYPT FILE SHARE-----------")
    }
  }

  public encryptContext = async (
    key: string,
    data: any,
    type: "hex" | "base64" | "passphrase" = "hex"
  ) => {
    try {
      const importedKey = await this.importAESKey(key, type)
      const iv = crypto.getRandomValues(new Uint8Array(12))
      const encrypted = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        importedKey,
        new TextEncoder().encode(data)
      )
      const ivAndEncrypted = new Uint8Array(iv.length + encrypted.byteLength)
      ivAndEncrypted.set(iv, 0)
      ivAndEncrypted.set(new Uint8Array(encrypted), iv.length)
      return this.bufferToBase64(ivAndEncrypted)
    } catch (error) {
      console.error("Encryption error:", error)
      return data
    }
  }

  public decryptContext = async (
    decryptKey: string,
    base64Data: string,
    fileMode?: boolean,
    isLogging: boolean = true,
    type: "hex" | "base64" | "passphrase" = "hex"
  ) => {
    if (isEmpty(base64Data)) {
      return base64Data
    }
    try {
      const binary = atob(base64Data)
      if (this.isSaltedBase64(binary)) {
        return await this.decryptContextCBC(
          decryptKey,
          binary,
          base64Data,
          fileMode
        )
      }
      return await this.decryptContextGCM(decryptKey, base64Data, type)
    } catch (error) {
      return base64Data // Return original data if decryption fails
    }
  }

  public base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64)
    const len = binaryString.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  }

  public async calculateSHA256(blob: Blob) {
    const hasher = await createSHA256()

    const totalChunks = Math.ceil(blob.size / CHUNK_SIZE_ENCRYPT)

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE_ENCRYPT
      const end = Math.min(start + CHUNK_SIZE_ENCRYPT, blob.size)
      const chunk = await blob.slice(start, end).arrayBuffer()

      hasher.update(new Uint8Array(chunk))
    }
    const hashBuffer = new TextEncoder().encode(hasher.digest()) // or decode hex properly
    return hashBuffer
  }

  public async coverPublicKeyToCryptoKey(
    key: string
  ): Promise<CryptoKey | null> {
    try {
      const keyData = this.pemToArrayBufferPublicKey(key)
      return await crypto.subtle.importKey(
        "spki",
        keyData,
        {
          name: "RSA-PSS",
          hash: "SHA-256",
        },
        true,
        ["verify"]
      )
    } catch (error) {
      console.log("Error importing importPublicKey:", error)
      return null
    }
  }

  public getFullAssetURI = (assetPath: string, extraParams?: any) => {
    let fileURL = `${API_URL}/api/assets/${encodeURIComponent(assetPath)}`
    if (extraParams?.conversation_id) {
      fileURL = `${API_URL}/api/invitee/assets/${encodeURIComponent(assetPath)}`
    }
    if (extraParams) {
      const queryString = new URLSearchParams(extraParams).toString()
      if (queryString) {
        fileURL += `?${queryString}`
      }
    }
    return fileURL
  }

  public getUserPrivateKey = async (
    userId: string
  ): Promise<CryptoKey | null> => {
    const keys = await getStoreData<{ id: string; privateKey: string }>(
      Stores.PrivateKeys
    )
    const keyPem = keys.find((el) => el.id === userId)?.privateKey || ""
    return keyPem ? await this.coverPrivateKeyToCryptoKey(keyPem) : null
  }

  public importAESKey = async (
    hexKey: string,
    type: "hex" | "base64" | "passphrase" = "hex"
  ): Promise<CryptoKey> => {
    const rawKey = await this.normalizeKey(hexKey, type)

    return crypto.subtle.importKey("raw", rawKey, "AES-GCM", true, [
      "encrypt",
      "decrypt",
    ])
  }

  public getFileHash = async (file: File): Promise<string> => {
    const sha256 = await createSHA256()
    const reader = file.stream().getReader()

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      sha256.update(value)
    }

    return sha256.digest()
  }

  public createSignature = async (
    privateKey: CryptoKey,
    data: Uint8Array
  ): Promise<ArrayBuffer> => {
    return crypto.subtle.sign(
      { name: "RSA-PSS", saltLength: 32 },
      privateKey,
      data
    )
  }

  public bufferToBase64 = (buffer: ArrayBuffer): string => {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
  }

  public getMultipartUploadPath = async (
    token: string,
    params: {
      fileName: string
      fileType: string
      totalChunks: number
      componentHistoryId: string
      componentType: string
      dbKey?: string
    }
  ): Promise<MultipartUploadPath> => {
    const query = new URLSearchParams({
      file_name: params.fileName,
      total_chunk: params.totalChunks.toString(),
      mime_type: params.fileType,
      project_component_history_id: params.componentHistoryId,
      type: params.componentType.toString(),
    })

    const res = await fetch(
      `${API_URL}/api/file/multipart-upload-path?${query}`,
      {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
          ...(params.dbKey ? { "x-db-key": params.dbKey } : {}),
        },
      }
    )

    return res.json()
  }

  public mergeUploadedChunks = async (
    token: string,
    uploadPath: MultipartUploadPath,
    parts: any[],
    dbKey?: string
  ): Promise<boolean> => {
    const res = await fetch(`${API_URL}/api/file/merge-multipart-upload-path`, {
      method: "POST",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
        ...(dbKey ? { "x-db-key": dbKey } : {}),
      },
      body: JSON.stringify({
        file_key: uploadPath.key,
        parts,
        upload_id: uploadPath.uploadId,
      }),
    })

    const result = await res.json()
    return result.message === "Upload completed"
  }

  private blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.readAsDataURL(blob)
    })
  }

  private parseMetadata = (headerValue: string | null): Metadata => {
    if (!headerValue) return { mimeType: "", encryptVersion: "" }

    try {
      const parsed = JSON.parse(headerValue)
      return {
        mimeType: parsed["mime-type"] ?? "",
        encryptVersion: parsed["encrypt-version"] ?? "",
      }
    } catch {
      return { mimeType: "", encryptVersion: "" }
    }
  }

  private extractFileName = (url: string): string => {
    return url.split("/").slice(-1)[0] || ""
  }

  private evpBytesToKey = (
    hexPassword: string,
    salt: Uint8Array,
    keySize = 32,
    ivSize = 16
  ) => {
    const passwordBytes = new TextEncoder().encode(hexPassword) // treat as UTF-8
    const saltBytes = salt
    let derived = new Uint8Array(0)
    let block = new Uint8Array(0)
    while (derived.length < keySize + ivSize) {
      const input = new Uint8Array(
        block.length + passwordBytes.length + saltBytes.length
      )
      input.set(block, 0)
      input.set(passwordBytes, block.length)
      input.set(saltBytes, block.length + passwordBytes.length)
      const md5 = forge.md.md5.create()
      md5.update(forge.util.binary.raw.encode(input))
      block = new Uint8Array(
        md5
          .digest()
          .getBytes()
          .split("")
          .map((c) => c.charCodeAt(0))
      )
      const newDerived = new Uint8Array(derived.length + block.length)
      newDerived.set(derived)
      newDerived.set(block, derived.length)
      derived = newDerived
    }
    return {
      key: derived.slice(0, keySize),
      iv: derived.slice(keySize, keySize + ivSize),
    }
  }

  private isSaltedBase64 = (base64Data: string): boolean => {
    return base64Data.startsWith("Salted")
  }

  private decryptContextCBC = async (
    decryptKey: string,
    binaryStr: string,
    base64Data: string,
    fileMode?: boolean
  ): Promise<any> => {
    const data = this.base64ToUint8Array(btoa(binaryStr)) // convert string -> Uint8Array
    const salt = data.slice(8, 16)
    const ciphertext = data.slice(16)

    const { key, iv } = this.evpBytesToKey(decryptKey, salt)

    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      key,
      { name: "AES-CBC" },
      false,
      ["decrypt"]
    )

    try {
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: "AES-CBC", iv },
        cryptoKey,
        ciphertext
      )
      if (fileMode) {
        return decryptedBuffer
      }
      return new TextDecoder().decode(decryptedBuffer)
    } catch (err) {
      return base64Data
    }
  }

  private decryptContextGCM = async (
    decryptKey: string,
    base64Data: string,
    type: "hex" | "base64" | "passphrase" = "hex"
  ): Promise<any> => {
    try {
      const encryptedData = this.base64ToUint8Array(base64Data)
      const iv = encryptedData.slice(0, 12)
      const ciphertext = encryptedData.slice(12)
      const importedKey = await this.decryptKeyByCrypto(decryptKey, type)

      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        importedKey,
        ciphertext
      )

      return new TextDecoder().decode(decrypted)
    } catch (err) {
      return base64Data
    }
  }

  public hexToUint8Array = (hex: string): Uint8Array => {
    if (hex.length % 2 !== 0) {
      throw new Error("Invalid hex string")
    }
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16)
    }
    return bytes
  }

  private normalizeKey = async (
    key: string,
    type: "hex" | "base64" | "passphrase" = "hex"
  ): Promise<Uint8Array> => {
    if (type === "passphrase") {
      const data = new TextEncoder().encode(key)
      const hash = await crypto.subtle.digest("SHA-256", data)
      return new Uint8Array(hash)
    }
    if (type === "base64") {
      try {
        return this.base64ToUint8Array(key)
      } catch (e) {}
    }
    return this.hexToUint8Array(key)
  }

  private pemToArrayBuffer(pem: string): ArrayBuffer {
    const privateKey = forge.pki.privateKeyFromPem(pem)
    const asn1PrivateKey = forge.pki.wrapRsaPrivateKey(
      forge.pki.privateKeyToAsn1(privateKey)
    ) // wrap  PKCS#8
    const pkcs8Der = forge.asn1.toDer(asn1PrivateKey)

    const buffer = new Uint8Array(pkcs8Der.length())
    for (let i = 0; i < pkcs8Der.length(); i++) {
      buffer[i] = pkcs8Der.at(i)
    }
    return buffer.buffer
  }

  private base64ToUint8Array = (base64: string): Uint8Array => {
    const CHUNK_SIZE = 8192 // Có thể tăng nếu RAM mạnh
    const binaryString = atob(base64)
    const len = binaryString.length
    const bytes = new Uint8Array(len)

    for (let i = 0; i < len; i += CHUNK_SIZE) {
      const sub = binaryString.slice(i, i + CHUNK_SIZE)
      for (let j = 0; j < sub.length; ++j) {
        bytes[i + j] = sub.charCodeAt(j)
      }
    }

    return bytes
  }

  private pemToArrayBufferPublicKey(pem: string): ArrayBuffer {
    const base64 = pem
      .replace(/-----BEGIN PUBLIC KEY-----/, "")
      .replace(/-----END PUBLIC KEY-----/, "")
      .replace(/\r?\n|\r/g, "")
    const binary = atob(base64)
    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes.buffer
  }

  public async verifyBlobHash(
    blob: Blob,
    expectedHashHex: string
  ): Promise<boolean> {
    const buffer = await blob.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer)
    const actualHex = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
    return actualHex === expectedHashHex
  }
}

export const encryptionGCMHelper = new EncryptionGCMHelper()
